/**
 * Typed registry of every backend URL the SPA calls.
 *
 * Every feature's `api.ts` imports from here so renames become a one-file
 * change instead of a grep-and-pray across the feature tree. The registry
 * mirrors `backend/pages/<page>/api.py` plus a few backend route
 * monoliths that have not yet been split.
 *
 * Conventions:
 *   - One namespace per backend domain (forecasts, centralBank, mars, ...).
 *   - Each entry is a function returning the URL string. Functions take a
 *     typed `params` object so callers get autocomplete and the query-string
 *     order is stable.
 *   - Path params (e.g. `:vintage_id`, `:email`) are always wrapped in
 *     `encodeURIComponent` so values like emails or names with reserved
 *     characters are safe.
 *   - Query strings use the {@link qs} helper which delegates to
 *     `URLSearchParams`. This means " " becomes `+`, not `%20` — Flask
 *     decodes both identically (HTTP/1.1 RFC 3986). The old hand-built
 *     URLs in some features used `encodeURIComponent` directly and emitted
 *     `%20`; the registry normalises to `+`. Server behaviour is unchanged.
 *
 * Adding an endpoint:
 *   1. Add a backend route under `backend/pages/<page>/api.py`.
 *   2. Add the corresponding entry here under the matching namespace.
 *   3. Reference it from the feature's `api.ts` — never inline a URL string.
 *   4. Add a snapshot test under `endpoints.test.ts`.
 */
import { qs, type QueryParams } from './query';

const enc = encodeURIComponent;

// ---------------------------------------------------------------------------
// Forecasts (S3-backed: nowcasts, inflation, recession) — economic-outlook tree
// `backend/pages/economic_outlook/api.py`
// ---------------------------------------------------------------------------

export const forecastsEndpoints = {
  nowcast: (params: { country: string; timeframe: string }): string =>
    `/api/economic-outlook/nowcasts${qs(params)}`,
  inflation: (params: {
    country: string;
    /** Backend reads `request.args.get('type')`. Must serialise as `type=`. */
    type: string;
    timeframe: string;
  }): string => `/api/economic-outlook/inflation${qs(params)}`,
  recession: (params: { country: string; timeframe: string }): string =>
    `/api/economic-outlook/recession${qs(params)}`,
} as const;

// ---------------------------------------------------------------------------
// Releases (nowcast releases + releases-tracker, under the economic-outlook tree)
// `backend/pages/economic_outlook/api.py`
// ---------------------------------------------------------------------------

/** Shared by `nowcastDelta` and `nowcastDailyDeltas`. */
export interface NowcastDeltaParams {
  country: string;
}

export const releasesEndpoints = {
  upcoming: (params?: { limit?: number }): string =>
    `/api/economic-outlook/releases/upcoming${qs(params)}`,
  nowcastWeek: (date: string, params: { country: string }): string =>
    `/api/economic-outlook/releases/week/${enc(date)}${qs(params)}`,
  nowcastDelta: (date: string, params: NowcastDeltaParams): string =>
    `/api/economic-outlook/nowcasts/delta/${enc(date)}${qs(params)}`,
  nowcastDailyDeltas: (date: string, params: NowcastDeltaParams): string =>
    `/api/economic-outlook/nowcasts/daily-deltas/${enc(date)}${qs(params)}`,
  // Releases-tracker indicator forecast-tracking chart (Upcoming-Release modal).
  indicatorChart: (indicator: string): string =>
    `/api/economic-outlook/releases/indicator/${enc(indicator)}/chart`,
  indicatorMetadata: (indicator: string): string =>
    `/api/economic-outlook/releases/indicator/${enc(indicator)}/metadata`,
} as const;

// ---------------------------------------------------------------------------
// Central Bank (speeches, scoring, voting, topics, hierarchical speakers)
// Central Bank routes (speeches, scoring, voting, topics).
// ---------------------------------------------------------------------------

export interface TimeseriesParams extends QueryParams {
  topic: string;
  period: string;
  fetchDays: number;
  version: string;
  bank?: string;
  speakerName?: string;
  speakerTitle?: string;
  documentType?: string;
}

/**
 * Shared by `fomcDual` (JSON and CSV). `format` selects the representation:
 * `json` (default, login-gated) or `csv` (Fulcrum-only attachment). The
 * backend branches on it inside one handler.
 */
export interface FomcDualParams extends QueryParams {
  year?: number;
  voters?: string;
  restrictions?: string;
  format?: 'json' | 'csv';
}

export const centralBankEndpoints = {
  speeches: (
    params?: QueryParams & {
      pageSize?: number;
      pageOffset?: number;
      selectedYear?: string;
      selectedMonth?: string;
      selectedTitle?: string;
      selectedBank?: string;
      selectedDocType?: string;
      mpRelevant?: string;
    },
  ): string => `/api/central-bank/speeches${qs(params)}`,
  upcomingSpeeches: (params?: QueryParams & { up_page?: number; bank?: string }): string =>
    `/api/central-bank/speeches/upcoming${qs(params)}`,
  /**
   * Selectable config slices. `include` is a CSV subset of
   * {banks, speakers, filters} (default all). Consolidates the former
   * bankConfig / hierarchicalSpeakers / filterOptions endpoints.
   */
  config: (params?: { include?: string; bank?: string }): string =>
    `/api/central-bank/config${qs(params)}`,
  speechText: (docId: number | string): string =>
    `/api/central-bank/speeches/${enc(String(docId))}/text`,
  documentTopics: (docId: number | string, params?: { version?: string }): string =>
    `/api/central-bank/speeches/${enc(String(docId))}/topics${qs(params)}`,
  downloadTranscriptPdf: (docId: number | string): string =>
    `/api/central-bank/speeches/${enc(String(docId))}/transcript.pdf`,
  timeseries: (params: TimeseriesParams): string => `/api/central-bank/timeseries${qs(params)}`,

  fomcDisplayContext: (params?: { sep_id?: string }): string =>
    `/api/central-bank/voting/display-context${qs(params)}`,
  fomcDual: (vintageId: string, params?: FomcDualParams): string =>
    `/api/central-bank/voting/vintages/${enc(vintageId)}/dual${qs(params)}`,
} as const;

// ---------------------------------------------------------------------------
// MARS (forecasts, fan/vintage data, portfolio, live)
// `backend/pages/mars/api.py`.
// ---------------------------------------------------------------------------

/** Shared by the `fanVintageMulti` endpoint and {@link buildFanMultiQuery}. */
export interface MarsFanMultiParams {
  /**
   * Fan-data variables. Caller passes the array of names to overlay; the
   * builder appends one `variables=` per entry plus `years_back`.
   */
  variables: readonly string[];
  years_back: number;
  /**
   * Opt into the top-level `ranges` field (global y-axis ranges per
   * variable), folding in the former /y_ranges route.
   */
  include?: 'ranges';
}

/** Helper: build the fan-data multi query with repeated `variables=` keys. */
function buildFanMultiQuery(params: MarsFanMultiParams): string {
  const sp = new URLSearchParams();
  for (const v of params.variables) sp.append('variables', v);
  sp.set('years_back', String(params.years_back));
  if (params.include) sp.set('include', params.include);
  return `?${sp.toString()}`;
}

export const marsEndpoints = {
  // Forecast series — one view-parametrized endpoint replacing the old
  // term_structure / nominal_real / mars_data / expected_returns routes.
  returns: (params: {
    view: 'term-structure' | 'nominal-real' | 'expected';
    timeframe?: string;
  }): string => `/api/mars/forecasts/returns${qs(params)}`,

  // High-frequency SVAR (financial-market shock decomposition)
  svar: (): string => `/api/mars/forecasts/svar`,

  // Decomposition (superuser)
  decomposition: (
    params: QueryParams & {
      mandate: string;
      asset: string;
      old_date?: string;
      new_date?: string;
    },
  ): string => `/api/mars/forecasts/decomposition${qs(params)}`,
  decompositionDates: (params: QueryParams & { mandate: string; limit?: number }): string =>
    `/api/mars/forecasts/decomposition/dates${qs(params)}`,
  decompositionConfig: (): string => `/api/mars/forecasts/decomposition/config`,

  // Fan data (vintage-keyed multi-variable forecasts)
  fanVintageList: (): string => `/api/mars/live/fan/vintages`,
  fanVintageMulti: (vintageDate: string, params: MarsFanMultiParams): string =>
    `/api/mars/live/fan/vintages/${enc(vintageDate)}/multi${buildFanMultiQuery(params)}`,

  // Vintage returns (DB-backed slider)
  returnsVintageList: (): string => `/api/mars/forecasts/returns/vintages`,
  returnsVintageSnapshot: (
    vintageDate: string,
    params?: { return_type?: 'all' | 'excess' | 'nominal' | 'real' },
  ): string => `/api/mars/forecasts/returns/vintages/${enc(vintageDate)}/snapshot${qs(params)}`,

  // Live (mars-live page)
  liveExpectedReturns: (
    params: QueryParams & {
      asset_class: string;
      horizon_months: number;
      return_type: 'excess' | 'nominal' | 'real';
    },
  ): string => `/api/mars/live/expected-returns${qs(params)}`,
  liveAssetClassVol: (): string => `/api/mars/live/asset-class-vol`,

  // Portfolio (mars-portfolio page)
  portfolioFunds: (): string => `/api/mars/portfolio/funds`,
  portfolioTimeseries: (fundId: string): string => `/api/mars/portfolio/${enc(fundId)}/timeseries`,
  portfolioExposures: (
    fundId: string,
    params: QueryParams & {
      timeframe: string;
      vol_timing?: boolean;
      constraints?: boolean;
      tc?: boolean;
    },
  ): string => `/api/mars/portfolio/${enc(fundId)}/exposures${qs(params)}`,
  portfolioPerformance: (
    fundId: string,
    params: QueryParams & { start_date?: string; end_date?: string },
  ): string => `/api/mars/portfolio/${enc(fundId)}/performance${qs(params)}`,
  portfolioRiskMetrics: (
    fundId: string,
    params: QueryParams & {
      type: string;
      timeframe: string;
      vol_timing?: boolean;
      constraints?: boolean;
      tc?: boolean;
    },
  ): string => `/api/mars/portfolio/${enc(fundId)}/risk-metrics${qs(params)}`,
} as const;

// ---------------------------------------------------------------------------
// Admin (login analytics, user activity)
// `backend/pages/admin/analytics/api.py`,
// `backend/pages/admin/activity/api.py`.
//
// Note: admin blueprints are mounted under `/api/admin/...` via
// `app_factory.py` rather than declared with that prefix on the route.
// ---------------------------------------------------------------------------

export interface PagedSearchParams extends QueryParams {
  sort_by: string;
  order: string;
  search?: string;
  limit: number;
  offset: number;
}

export interface DaysParams {
  days: number;
}

export interface DaysLimitParams extends QueryParams {
  days: number;
  limit: number;
}

export type ActivityTopDimension = 'pages' | 'events' | 'documents' | 'countries';

export interface ActivityTopParams extends DaysLimitParams {
  dimension: ActivityTopDimension;
}

export const adminEndpoints = {
  loginAnalytics: {
    summary: (): string => `/api/admin/login-analytics/summary`,
    daily: (params: DaysParams): string => `/api/admin/login-analytics/daily${qs(params)}`,
    users: (params: PagedSearchParams): string => `/api/admin/login-analytics/users${qs(params)}`,
    user: (email: string): string => `/api/admin/login-analytics/user/${enc(email)}`,
  },
  activity: {
    summary: (): string => `/api/admin/user-activity/summary`,
    daily: (params: DaysParams): string => `/api/admin/user-activity/daily${qs(params)}`,
    top: (params: ActivityTopParams): string => `/api/admin/user-activity/top${qs(params)}`,
    users: (params: PagedSearchParams): string => `/api/admin/user-activity/users${qs(params)}`,
    user: (email: string): string => `/api/admin/user-activity/user/${enc(email)}`,
  },
} as const;

// ---------------------------------------------------------------------------
// Activity tracking + meta (small loose endpoints)
// `backend/pages/admin/activity/api.py` (`user_activity_bp`).
// ---------------------------------------------------------------------------

export const activityEndpoints = {
  // POST ingest for the user-activity journal (`backend/pages/admin/api.py`).
  // The path deliberately avoids tracker vocabulary ("track", "analytics",
  // ...): privacy blocklists and corporate web filters match those URL
  // tokens and silently swallow the POSTs — which is how two users' activity
  // vanished in July 2026. The backend keeps `/api/track-activity` as a
  // legacy alias for cached bundles; new code must use this path.
  track: (): string => `/api/journal`,
} as const;

// ---------------------------------------------------------------------------
// Config (feature flags, runtime country lists, etc.)
// `backend/pages/economic_outlook/api.py`,
// `backend/pages/central_bank/api.py` (bank_config).
// ---------------------------------------------------------------------------

export const configEndpoints = {
  economicOutlookCountries: (): string => `/api/economic-outlook/countries`,
} as const;

// ---------------------------------------------------------------------------
// Auth (login, logout, status, dummy login)
// `backend/infra/auth/simple_auth.py` and Cognito hosted-UI bridge.
// ---------------------------------------------------------------------------

export const authEndpoints = {
  status: (): string => `/auth/status`,
  config: (): string => `/auth/config`,
  logout: (): string => `/logout`,
  cognitoLogin: (params?: QueryParams & { next?: string }): string => `/cognito/login${qs(params)}`,
  dummyLogin: (): string => `/dummy/login`,
} as const;

// ---------------------------------------------------------------------------
// Top-level barrel
// ---------------------------------------------------------------------------

export const endpoints = {
  forecasts: forecastsEndpoints,
  releases: releasesEndpoints,
  centralBank: centralBankEndpoints,
  mars: marsEndpoints,
  admin: adminEndpoints,
  activity: activityEndpoints,
  config: configEndpoints,
  auth: authEndpoints,
} as const;
